import { createFileRoute } from "@tanstack/react-router";
import {
  IndustryMarketingPage,
  industryMarketingHead,
  type IndustryFAQ,
} from "@/components/IndustryMarketingPage";

const FAQS: IndustryFAQ[] = [
  {
    q: "How do I get more HVAC leads in Colorado Springs?",
    a: "The fastest path is a tuned Google Business Profile + local SEO so you show up in the Map Pack for searches like 'HVAC repair near me' and 'AC repair Colorado Springs', backed by a website that's fast and converts mobile callers. We layer Google Ads on top during slow seasons to keep your schedule full year-round.",
  },
  {
    q: "What's the best marketing channel for HVAC companies in Colorado?",
    a: "For most Colorado HVAC companies the highest-ROI channel is local SEO + Google Maps — high intent, low cost-per-lead, and recurring. Google Ads is the best layer for emergency/seasonal demand, and GEO AI is becoming critical for showing up when customers ask ChatGPT or Gemini for an HVAC recommendation. Full breakdown: /answers/best-marketing-channel-hvac-colorado.",
  },
  {
    q: "How long does HVAC SEO take to work?",
    a: "Most Colorado HVAC clients see meaningful Map Pack movement in 60–120 days and stronger organic traffic within 4–6 months. Markets like Colorado Springs and Denver are competitive, so consistent monthly work is what compounds — not one-off projects.",
  },
  {
    q: "Should I run Google Ads or focus on SEO first for my HVAC business?",
    a: "If you need leads this week, start Google Ads. If you want a long-term lead engine that doesn't shut off when ad spend stops, start SEO. Most successful Colorado HVAC companies run both — ads for immediate flow, SEO for the durable foundation.",
  },
  {
    q: "How do I rank in the Google Map Pack for HVAC in Colorado?",
    a: "Three things matter most: a fully optimized Google Business Profile (categories, services, photos, posts), a steady stream of real Google reviews, and proximity-relevance signals from on-page SEO and local citations. We handle all three as part of our local SEO service.",
  },
];

export const Route = createFileRoute("/hvac-marketing-colorado")({
  head: () => industryMarketingHead("HVAC", "HVAC", "hvac-marketing-colorado", FAQS),
  component: () => (
    <IndustryMarketingPage
      industry="HVAC"
      industryLower="HVAC"
      slug="hvac-marketing-colorado"
      subheadline="We help Colorado HVAC companies fill their schedule year-round — slow seasons and all."
      painPoints={[
        "Slow seasons (spring/fall) with no steady flow of inbound leads",
        "Losing calls to bigger HVAC brands with bigger ad budgets",
        "Google Maps rankings dropping because the Google Business Profile isn't optimized",
      ]}
      serviceDescriptions={{
        seo: "Rank for 'HVAC repair', 'AC repair', and 'furnace install' in every Colorado market you serve.",
        googleMaps: "Dominate the local Map Pack so emergency HVAC calls come to you first.",
        webDesign: "Fast, mobile-first HVAC websites built to convert calls — not just look pretty.",
        ppc: "Profitable Google Ads tuned to your service area, season, and capacity.",
        geoAi: "Get cited when Coloradans ask ChatGPT or Gemini 'best HVAC company near me.'",
        socialMedia: "Local trust-building content that warms up future maintenance customers.",
      }}
      testimonial={{
        quote:
          "Our phone hasn't stopped ringing since they rebuilt our site and dialed in our SEO. Best marketing investment we've ever made.",
        role: "HVAC Company Owner",
        city: "Colorado Springs",
      }}
      faqs={FAQS}
      industrySpokes={[
        {
          "to": "/hvac-seo-colorado",
          "label": "HVAC SEO in Colorado",
          "description": "Rank for AC repair, furnace install, and emergency HVAC searches across Colorado."
        },
        {
          "to": "/hvac-google-maps-colorado",
          "label": "HVAC Google Maps in Colorado",
          "description": "Map Pack ranking and GBP optimization for Colorado HVAC companies."
        },
        {
          "to": "/hvac-web-design-colorado",
          "label": "HVAC web design in Colorado",
          "description": "Hand-coded HVAC sites built to convert mobile emergency callers."
        }
      ]}
      cityHubLinks={[
        {
          "to": "/colorado-springs",
          "label": "HVAC marketing in Colorado Springs",
          "description": "How we help Springs HVAC companies own the Map Pack and rank locally."
        },
        {
          "to": "/denver-digital-marketing",
          "label": "HVAC marketing in Denver",
          "description": "Compete in Colorado's most saturated HVAC market — neighborhood by neighborhood."
        },
        {
          "to": "/fort-collins-digital-marketing",
          "label": "HVAC marketing in Fort Collins",
          "description": "Northern Colorado HVAC SEO, web, and Maps for Fort Collins shops."
        }
      ]}
    />
  ),
});
